/**
 * TanStack Router Configuration
 *
 * Configuração centralizada de rotas com guards de autenticação
 * e lazy loading otimizado para performance.
 *
 * Estrutura:
 * - Rotas públicas: /auth/*
 * - Rotas protegidas: /, /daily/*, /health/*, /expenses/*
 * - Rotas de onboarding: /onboarding/* (apenas usuários não completaram)
 *
 * @see https://tanstack.com/router/latest
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  Outlet,
  lazyRouteComponent,
} from '@tanstack/react-router';
import { lazy } from 'react';
import { useAuthStore } from '@/shared/stores/auth-store';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import { RootLayout } from '@/shared/components/layout/RootLayout';

// ============================================================================
// CONSTANTS
// ============================================================================

const SESSION_INIT_TIMEOUT = 3000; // 3 segundos máximo para aguardar sessão

// ============================================================================
// AUTH GUARDS (centralizados para reutilização)
// ============================================================================

/**
 * Aguarda inicialização da sessão antes de verificar autenticação
 * Previne race condition onde router roda antes do session load
 *
 * @returns Promise que resolve quando sessão estiver carregada
 */
async function waitForSessionInit(): Promise<void> {
  const store = useAuthStore.getState();

  if (store._sessionInitialized) {
    return; // Já inicializado
  }

  // Aguarda até 3 segundos pela inicialização
  await new Promise<void>((resolve) => {
    const timeout = setTimeout(() => resolve(), SESSION_INIT_TIMEOUT);
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state._sessionInitialized) {
        clearTimeout(timeout);
        unsubscribe();
        resolve();
      }
    });
  });
}

/**
 * Guard para rotas protegidas (requer autenticação + onboarding completo)
 * Redireciona para login se não autenticado, ou onboarding se não completou
 */
async function requireAuthAndOnboarding() {
  await waitForSessionInit();

  const { user } = useAuthStore.getState();

  if (!user) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/auth/login' });
  }

  if (!user.onboardingCompleted) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/onboarding/welcome' });
  }
}

/**
 * Guard para rotas de onboarding (requer autenticação, bloqueia se já completou)
 * Redireciona para login se não autenticado, ou home se já completou onboarding
 */
async function requireAuthButNotOnboarded() {
  await waitForSessionInit();

  const { user } = useAuthStore.getState();

  if (!user) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/auth/login' });
  }

  if (user.onboardingCompleted) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/' });
  }
}

// ============================================================================
// LAZY LOADED COMPONENTS
// ============================================================================

// Auth pages (lazy para reduzir bundle inicial - named exports)
const Login = lazy(() => import('@/pages/auth/Login').then((m) => ({ default: m.Login })));
const Signup = lazy(() => import('@/pages/auth/Signup').then((m) => ({ default: m.Signup })));
const ForgotPassword = lazy(() =>
  import('@/pages/auth/ForgotPassword').then((m) => ({ default: m.ForgotPassword }))
);
const ResetPassword = lazy(() =>
  import('@/pages/auth/ResetPassword').then((m) => ({ default: m.ResetPassword }))
);
const VerifyEmail = lazy(() =>
  import('@/pages/auth/VerifyEmail').then((m) => ({ default: m.VerifyEmail }))
);
const VerifyEmailPrompt = lazy(() =>
  import('@/pages/auth/VerifyEmailPrompt').then((m) => ({ default: m.VerifyEmailPrompt }))
);
const AuthCallback = lazy(() =>
  import('@/pages/auth/AuthCallback').then((m) => ({ default: m.AuthCallback }))
);

// Onboarding pages (lazy para reduzir bundle inicial - named exports)
const Welcome = lazy(() =>
  import('@/pages/onboarding/Welcome').then((m) => ({ default: m.Welcome }))
);
const DogProfile = lazy(() =>
  import('@/pages/onboarding/DogProfile').then((m) => ({ default: m.DogProfile }))
);
const Completed = lazy(() =>
  import('@/pages/onboarding/Completed').then((m) => ({ default: m.Completed }))
);

// Home page (lazy - default export)
const Home = lazy(() => import('@/pages/home/home'));

// ============================================================================
// ROOT ROUTE
// ============================================================================

const rootRoute = createRootRoute({
  component: RootLayout,
});

// ============================================================================
// AUTH ROUTES (públicas)
// ============================================================================

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  component: Outlet,
});

const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/login',
  component: Login,
});

const signupRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/signup',
  component: Signup,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/forgot-password',
  component: ForgotPassword,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/reset-password',
  component: ResetPassword,
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) ?? '',
  }),
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/verify-email',
  component: VerifyEmail,
  validateSearch: (search: Record<string, unknown>) => ({
    error: search.error as string | undefined,
    email: search.email as string | undefined,
  }),
});

const verifyEmailPromptRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/verify-email-prompt',
  component: VerifyEmailPrompt,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/callback/$provider',
  component: AuthCallback,
});

// ============================================================================
// ONBOARDING ROUTES (requer auth, bloqueia se já completou)
// ============================================================================

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  component: Outlet,
  beforeLoad: requireAuthButNotOnboarded, // Guard centralizado
});

const onboardingWelcomeRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: '/welcome',
  component: Welcome,
});

const onboardingDogProfileRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: '/dog-profile',
  component: DogProfile,
});

const onboardingCompletedRoute = createRoute({
  getParentRoute: () => onboardingRoute,
  path: '/completed',
  component: Completed,
});

// ============================================================================
// APP LAYOUT ROUTE (wrapper para rotas protegidas com DogHeader + BottomNav)
// ============================================================================

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app-layout',
  component: AppLayout,
  beforeLoad: requireAuthAndOnboarding, // Guard centralizado para todas as rotas filhas
});

// ============================================================================
// PROTECTED ROUTES (requer auth + onboarding completo)
// ============================================================================

// Home route
const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: Home,
});

// Daily routes
const dailyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/daily',
  component: Outlet,
});

const dailyIndexRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/daily/index'), 'default'),
});

const urineRegisterRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/urine/register',
  component: lazyRouteComponent(() => import('@/pages/home/urine/register'), 'default'),
});

const urineHistoryRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/urine/history',
  component: lazyRouteComponent(() => import('@/pages/home/urine/history'), 'default'),
});

const poopRegisterRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/poop/register',
  component: lazyRouteComponent(() => import('@/pages/home/poop/register'), 'default'),
});

const poopHistoryRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/poop/history',
  component: lazyRouteComponent(() => import('@/pages/home/poop/history'), 'default'),
});

const waterRegisterRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/water/register',
  component: lazyRouteComponent(() => import('@/pages/home/water/register'), 'default'),
});

const waterHistoryRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/water/history',
  component: lazyRouteComponent(() => import('@/pages/home/water/history'), 'default'),
});

const incidentsRegisterRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/incidents/register',
  component: lazyRouteComponent(() => import('@/pages/home/incidents/register'), 'default'),
});

const incidentsHistoryRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/incidents/history',
  component: lazyRouteComponent(() => import('@/pages/home/incidents/history'), 'default'),
});

const interactionsRegisterRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/interactions/register',
  component: lazyRouteComponent(() => import('@/pages/home/interactions/register'), 'default'),
});

const interactionsHistoryRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/interactions/history',
  component: lazyRouteComponent(() => import('@/pages/home/interactions/history'), 'default'),
});

const symptomsRegisterRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/symptoms/register',
  component: lazyRouteComponent(() => import('@/pages/home/symptoms/register'), 'default'),
});

const symptomsHistoryRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/symptoms/history',
  component: lazyRouteComponent(() => import('@/pages/home/symptoms/history'), 'default'),
});

const groomingRegisterRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/grooming/register',
  component: lazyRouteComponent(() => import('@/pages/home/grooming/register'), 'default'),
});

const groomingHistoryRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/grooming/history',
  component: lazyRouteComponent(() => import('@/pages/home/grooming/history'), 'default'),
});

const weightRegisterRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/weight/register',
  component: lazyRouteComponent(() => import('@/pages/home/weight/register'), 'default'),
});

const weightHistoryRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/weight/history',
  component: lazyRouteComponent(() => import('@/pages/home/weight/history'), 'default'),
});

const behaviorRegisterRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/behavior/register',
  component: lazyRouteComponent(() => import('@/pages/home/behavior/register'), 'default'),
});

const mealsRegisterRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/meals/register',
  component: lazyRouteComponent(() => import('@/pages/home/meals/register'), 'default'),
});

const mealsHistoryRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/meals/history',
  component: lazyRouteComponent(() => import('@/pages/home/meals/history'), 'default'),
});

const walksHistoryRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/walks/history',
  component: lazyRouteComponent(() => import('@/pages/home/walks/history'), 'default'),
});

const reportsConfigRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/reports/config',
  component: lazyRouteComponent(() => import('@/pages/daily/reports/config'), 'default'),
});

const reportsViewRoute = createRoute({
  getParentRoute: () => dailyRoute,
  path: '/reports/view',
  component: lazyRouteComponent(() => import('@/pages/daily/reports/view'), 'default'),
});

// Health routes
const healthRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/health',
  component: Outlet,
});

const healthIndexRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/health/index'), 'default'),
});

const examsRegisterRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/exams/register',
  component: lazyRouteComponent(() => import('@/pages/health/exams/register'), 'default'),
});

const examsHistoryRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/exams/history',
  component: lazyRouteComponent(() => import('@/pages/health/exams/history'), 'default'),
});

const vaccinationsRegisterRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/vaccinations/register',
  component: lazyRouteComponent(() => import('@/pages/health/vaccinations/register'), 'default'),
});

const vaccinationsListRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/vaccinations/list',
  component: lazyRouteComponent(() => import('@/pages/health/vaccinations/list'), 'default'),
});

// Main antiparasitics page (register form with recent history)
const antiparasiticsRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/antiparasitics',
  component: lazyRouteComponent(
    () => import('@/pages/health/antiparasitics/register'),
    'default'
  ),
});

// Full history page
const antiparasiticsHistoryRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/antiparasitics/history',
  component: lazyRouteComponent(
    () => import('@/pages/health/antiparasitics/list'),
    'default'
  ),
});

// Diet routes
const dietCreateRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/diet/create',
  component: lazyRouteComponent(() => import('@/pages/health/diet/create'), 'default'),
});

const dietListRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/diet/list',
  component: lazyRouteComponent(() => import('@/pages/health/diet/list'), 'default'),
});

const dietValidateRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/diet/validate',
  component: lazyRouteComponent(() => import('@/pages/health/diet/validate'), 'default'),
});

const mealsListRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/diet/meals/list',
  component: lazyRouteComponent(() => import('@/pages/health/diet/meals/list'), 'default'),
});

const mealsValidateRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/diet/meals/validate',
  component: lazyRouteComponent(() => import('@/pages/health/diet/meals/validate'), 'default'),
});

// Medications routes
const medicationsCreateRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/medications/create',
  component: lazyRouteComponent(() => import('@/pages/health/medications/create'), 'default'),
});

const medicationsValidateRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/medications/validate',
  component: lazyRouteComponent(() => import('@/pages/health/medications/validate'), 'default'),
});

const medicationsRegisterRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/medications/register',
  component: lazyRouteComponent(() => import('@/pages/health/medications/register'), 'default'),
});

const medicationsListRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/medications/list',
  component: lazyRouteComponent(() => import('@/pages/health/medications/list'), 'default'),
});

const medicationsHistoryRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/medications/history',
  component: lazyRouteComponent(() => import('@/pages/health/medications/history'), 'default'),
});

// Appointments routes
const appointmentsListRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/appointments/list',
  component: lazyRouteComponent(() => import('@/pages/health/appointments/list'), 'default'),
});

// Health reports route
const healthReportsViewRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/reports/view',
  component: lazyRouteComponent(() => import('@/pages/health/reports/view'), 'default'),
});

// Therapies route
const therapiesRegisterRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/therapies/register',
  component: lazyRouteComponent(() => import('@/pages/health/therapies/register'), 'default'),
});

const therapiesListRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/therapies/list',
  component: lazyRouteComponent(() => import('@/pages/health/therapies/list'), 'default'),
});

// Dental routes
const dentalRegisterRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/dental/register',
  component: lazyRouteComponent(() => import('@/pages/health/dental/register'), 'default'),
});

const dentalListRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/dental/list',
  component: lazyRouteComponent(() => import('@/pages/health/dental/list'), 'default'),
});

// Checkup route
const checkupRegisterRoute = createRoute({
  getParentRoute: () => healthRoute,
  path: '/checkup/register',
  component: lazyRouteComponent(() => import('@/pages/health/checkup/register'), 'default'),
});

// Expenses routes
const expensesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/expenses',
  component: Outlet,
});

const expensesIndexRoute = createRoute({
  getParentRoute: () => expensesRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/expenses/index'), 'default'),
});

const expensesRegisterRoute = createRoute({
  getParentRoute: () => expensesRoute,
  path: '/register',
  component: lazyRouteComponent(() => import('@/pages/expenses/register'), 'default'),
});

// Settings routes
const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  component: Outlet,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/settings/index'), 'default'),
});

const dogEditRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/dog/edit',
  component: lazyRouteComponent(() => import('@/pages/settings/dog/edit'), 'default'),
  validateSearch: (search: Record<string, unknown>) => ({
    dogId: search.dogId as string | undefined,
  }),
});

const dogAddRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/dog/add',
  component: lazyRouteComponent(() => import('@/pages/settings/dog/add'), 'default'),
});

const profileEditRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/profile',
  component: lazyRouteComponent(() => import('@/pages/settings/profile/edit'), 'default'),
});

const privacyRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/privacy',
  component: lazyRouteComponent(() => import('@/pages/settings/privacy/index'), 'default'),
});

const referenceValuesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/reference-values',
  component: lazyRouteComponent(() => import('@/pages/settings/reference-values/index'), 'default'),
});

// Notifications route
const notificationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/notifications',
  component: lazyRouteComponent(() => import('@/pages/notifications/index'), 'default'),
});

// Reminders routes
const remindersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/reminders',
  component: Outlet,
});

const remindersIndexRoute = createRoute({
  getParentRoute: () => remindersRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/reminders/index'), 'default'),
});

const reminderDetailRoute = createRoute({
  getParentRoute: () => remindersRoute,
  path: '/$id',
  component: lazyRouteComponent(() => import('@/pages/reminders/detail'), 'default'),
});

// Gamification routes
const gamificationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/gamification',
  component: Outlet,
});

const gamificationHistoryRoute = createRoute({
  getParentRoute: () => gamificationRoute,
  path: '/history',
  component: lazyRouteComponent(() => import('@/pages/gamification/history'), 'default'),
});

// Community routes
const communityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/community',
  component: Outlet,
});

const communityIndexRoute = createRoute({
  getParentRoute: () => communityRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/community/index'), 'default'),
});

const communityRecordRoute = createRoute({
  getParentRoute: () => communityRoute,
  path: '/record',
  component: lazyRouteComponent(() => import('@/pages/community/record'), 'default'),
  validateSearch: (search: Record<string, unknown>) => ({
    template: search.template as string | undefined,
  }),
});

// ============================================================================
// ROUTE TREE
// ============================================================================

const routeTree = rootRoute.addChildren([
  // Rotas públicas
  authRoute.addChildren([
    loginRoute,
    signupRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
    verifyEmailPromptRoute,
    authCallbackRoute,
  ]),

  // Rotas de onboarding (auth + !onboardingCompleted)
  onboardingRoute.addChildren([
    onboardingWelcomeRoute,
    onboardingDogProfileRoute,
    onboardingCompletedRoute,
  ]),

  // Rotas protegidas com AppLayout (auth + onboardingCompleted)
  appLayoutRoute.addChildren([
    indexRoute,
    dailyRoute.addChildren([
      dailyIndexRoute,
      urineRegisterRoute,
      urineHistoryRoute,
      poopRegisterRoute,
      poopHistoryRoute,
      waterRegisterRoute,
      waterHistoryRoute,
      incidentsRegisterRoute,
      incidentsHistoryRoute,
      interactionsRegisterRoute,
      interactionsHistoryRoute,
      symptomsRegisterRoute,
      symptomsHistoryRoute,
      groomingRegisterRoute,
      groomingHistoryRoute,
      weightRegisterRoute,
      weightHistoryRoute,
      behaviorRegisterRoute,
      mealsRegisterRoute,
      mealsHistoryRoute,
      walksHistoryRoute,
      reportsConfigRoute,
      reportsViewRoute,
    ]),
    healthRoute.addChildren([
      healthIndexRoute,
      examsRegisterRoute,
      examsHistoryRoute,
      vaccinationsRegisterRoute,
      vaccinationsListRoute,
      antiparasiticsRoute,
      antiparasiticsHistoryRoute,
      dietCreateRoute,
      dietListRoute,
      dietValidateRoute,
      mealsListRoute,
      mealsValidateRoute,
      medicationsCreateRoute,
      medicationsValidateRoute,
      medicationsRegisterRoute,
      medicationsListRoute,
      medicationsHistoryRoute,
      appointmentsListRoute,
      healthReportsViewRoute,
      therapiesRegisterRoute,
      therapiesListRoute,
      dentalRegisterRoute,
      dentalListRoute,
      checkupRegisterRoute,
    ]),
    expensesRoute.addChildren([
      expensesIndexRoute,
      expensesRegisterRoute,
    ]),
    settingsRoute.addChildren([
      settingsIndexRoute,
      dogEditRoute,
      dogAddRoute,
      profileEditRoute,
      privacyRoute,
      referenceValuesRoute,
    ]),
    notificationsRoute,
    remindersRoute.addChildren([
      remindersIndexRoute,
      reminderDetailRoute,
    ]),
    gamificationRoute.addChildren([
      gamificationHistoryRoute,
    ]),
    communityRoute.addChildren([
      communityIndexRoute,
      communityRecordRoute,
    ]),
  ]),
]);

// ============================================================================
// ROUTER INSTANCE
// ============================================================================

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent', // Preload on hover/focus para melhor UX
});

// ============================================================================
// TYPE AUGMENTATION
// ============================================================================

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
